import React from 'react'
import { isEmpty, isNil, isObject, sortBy, startCase } from 'lodash'

import { Pill } from '@/components/pills/Pill'
import { formatCustomColumns } from '@/components/helpers/tableColumnHelpers'
import { DEFAULT_CALL_COLUMNS } from '@/views/Calls/callColumns'
import {
  formatSummaryTableContent,
  getSummaryValue,
  isCamelCase,
  camelCaseToString,
  toTitleCase,
} from '@/utils/helpers'

// If saved custom columns are returned from the API, merge and format them with the default columns
// otherwise return the default
export const getMemoizedColumns = (customColumns, defaultColumns) => {
  return !isEmpty(customColumns)
    ? formatCustomColumns(defaultColumns, customColumns, true)
    : defaultColumns.filter((value) => !value?.is_hidden)
}

export const CALL_SUMMARY_PARENT = 'call_summary'
export const EXTRACTED_DATA_PARENT = 'extracted_data'

// Row fields a call always carries that aren't part of DEFAULT_CALL_COLUMNS: the two
// locked identifier columns CallSearchPage prepends, and the fields the table reads
// off the row directly. A notetaker field named after one of these would be shadowed
// by the call's own value exactly like a default-column collision, so it namespaces too.
const RESERVED_CALL_FIELDS = ['call_id', 'message_id', 'record_type', 'is_ai_call', 'scorecards']

const RESERVED_ROW_ACCESSORS = [
  ...DEFAULT_CALL_COLUMNS.map(({ accessor }) => accessor),
  ...RESERVED_CALL_FIELDS,
].map((accessor) => accessor.toLowerCase())

// A notetaker field can be named the same as a field the call itself carries - an org
// with a "Disposition" field alongside the call's own disposition. Both values are real
// and both are worth showing, so the notetaker one is namespaced under its parent rather
// than shadowed, the same way tag categories are keyed `tags-<category>`.
export const collidesWithDefaultColumn = (key) => RESERVED_ROW_ACCESSORS.includes(key.toLowerCase())

export const getSummaryAccessor = (key, parentAccessor) =>
  collidesWithDefaultColumn(key) ? `${parentAccessor}-${key}` : key

// Summary and extracted_data keys are flattened onto the call so the table can read
// them by accessor. The call's own fields still win, but a colliding key now lands in
// its namespaced slot instead of being dropped on the floor.
export const flattenSummaryOntoCall = (call, summary = {}, parentAccessor) => {
  const flattenedSummary = {}
  Object.entries(summary).forEach(([key, value]) => {
    flattenedSummary[getSummaryAccessor(key, parentAccessor)] = value
  })

  return { ...flattenedSummary, ...call }
}

export const getDynamicColumns = (calls = [], fieldName = CALL_SUMMARY_PARENT) => {
  const columnsFoundInSummariesToRender = calls.reduce((acc, call) => {
    if (!isEmpty(call[fieldName]) && isObject(call[fieldName])) {
      acc.push(...Object.keys(call[fieldName]))
    }
    return acc
  }, [])

  return [...new Set(columnsFoundInSummariesToRender)]
}

export const formatDynamicColumnLabel = (column) => {
  if (isCamelCase(column)) {
    return toTitleCase(camelCaseToString(column))
  }
  if (column.includes('_')) {
    return startCase(column)
  }
  return toTitleCase(column)
}

// Insert all the dynamic columns where the parent column is listed
export const getCompleteColumns = (
  columns,
  dynamicColumns,
  parentAccessor = CALL_SUMMARY_PARENT
) => {
  let completeColumns = columns
  const indexOfParentColumn = columns.findIndex((column) => column.accessor === parentAccessor)

  if (indexOfParentColumn !== -1) {
    // Only a colliding column needs the suffix, and it comes from the parent's own label
    // ('Notes', 'Extracted Data') so the two stay in sync.
    const parentLabel = columns[indexOfParentColumn].label
    const beginningColumns = columns.slice(0, indexOfParentColumn)
    const middleColumns = dynamicColumns.map((column) => ({
      label: collidesWithDefaultColumn(column)
        ? `${formatDynamicColumnLabel(column)} (${parentLabel})`
        : formatDynamicColumnLabel(column),
      accessor: getSummaryAccessor(column, parentAccessor),
      is_hidden: false,
      isSortable: false,
      parent: parentAccessor,
      isCopiable: true,
      isSearchable: true,
      format: (value) => {
        if (value) {
          return formatSummaryTableContent(value)
        }

        return null
      },
      formatCopyValue: (value) => getSummaryValue(value),
    }))

    const trailingColumns = columns.slice(indexOfParentColumn + 1)
    completeColumns = [...beginningColumns, ...middleColumns, ...trailingColumns]
  }

  return completeColumns
}

// Columns whose values come from lazily-loaded summary data (see fetchSummariesForCallIds).
// Their values are only present for rendered rows, so searching them requires loading
// summaries for the whole result set first.
export const SUMMARY_DERIVED_PARENTS = [CALL_SUMMARY_PARENT, EXTRACTED_DATA_PARENT]

export const getSummarySearchAccessors = (columns = []) =>
  columns
    .filter((column) => SUMMARY_DERIVED_PARENTS.includes(column.parent))
    .map((column) => column.accessor)

// makes column names for the scores part of a call
export const getScorecardsCustomColumns = (scorecards) => {
  const scorecardNames = scorecards?.map((scorecard) => scorecard.label)
  const newColumns = []
  scorecardNames?.forEach((scorecardName) => {
    newColumns.push(`${scorecardName} - Overall QA Score`)
  })
  return newColumns
}

// inserts scores columns (similar to getCompleteColumns()
export const getCompleteColumnsWithScores = (columns, dynamicColumns) => {
  let completeColumns = columns
  const indexOfScoresSection = columns.findIndex((column) => column.accessor === 'scores')

  if (indexOfScoresSection !== -1) {
    const beginningColumns = columns.slice(0, indexOfScoresSection)
    const middleColumns = dynamicColumns.map((column) => ({
      label: column,
      accessor: column,
      is_hidden: false,
      isSortable: false,
      isCopiable: true,
      isSearchable: true,
      parent: 'scores',
      format: (value) => {
        if (isNil(value)) return null
        if (column.includes('QA Score')) return `${value.toFixed(2)}%`
        return value
      },
    }))
    const trailingColumns = columns.slice(indexOfScoresSection + 1)
    completeColumns = [...beginningColumns, ...middleColumns, ...trailingColumns]
  }

  return completeColumns
}

export const enrichCallsWithTagCategories = (callsData, tagCategoriesData = []) => {
  if (isEmpty(tagCategoriesData)) {
    return callsData
  }

  const tagToCategoryMap = {}
  tagCategoriesData.forEach((category) => {
    category.options.forEach((option) => {
      tagToCategoryMap[option.label] = category.label
    })
  })

  return callsData.map((call) => {
    const updatedCall = { ...call }

    const tags = call.tags ?? []
    tags.forEach((tagName) => {
      const categoryLabel = tagToCategoryMap[tagName]
      if (!categoryLabel) {
        return
      }
      const accessor = `tags-${categoryLabel}`
      if (!updatedCall[accessor]) {
        updatedCall[accessor] = []
      }
      updatedCall[accessor].push(tagName)
    })

    return updatedCall
  })
}

// A dynamic column the user has never saved has no entry in savedColumns, and a raw
// findIndex would score it -1 and sort it ahead of every saved column. Sort it after
// them instead, so it keeps the position the table generated it in until the user
// drags it somewhere and saves.
const getSavedColumnIndex = (savedColumns, accessor) => {
  const savedIndex = savedColumns.findIndex((column) => column.accessor === accessor)

  return savedIndex === -1 ? savedColumns.length : savedIndex
}

export const filterExtraColumns = (completeColumns, savedColumns) => {
  return completeColumns
    .filter((column) => {
      const savedColumn = savedColumns.find((_column) => _column.accessor === column.accessor)
      if (savedColumn) {
        return !savedColumn.is_hidden
      }
      return !column.is_hidden
    })
    .sort((columnA, columnB) => {
      if (columnA.parent && columnB.parent && columnA.parent === columnB.parent) {
        return (
          getSavedColumnIndex(savedColumns, columnA.accessor) -
          getSavedColumnIndex(savedColumns, columnB.accessor)
        )
      }
      return savedColumns.indexOf(columnA) - savedColumns.indexOf(columnB)
    })
}

export const getTagCategoryColumns = (tagCategoriesData = []) => {
  return tagCategoriesData
    .filter((category) => !isEmpty(category.options))
    .map((category) => category.label)
}

export const getCompleteColumnsWithTags = (columns, dynamicTagColumns) => {
  let completeColumns = columns
  const indexOfTagsSection = columns.findIndex((column) => column.accessor === 'tags')

  if (indexOfTagsSection !== -1 && !isEmpty(dynamicTagColumns)) {
    const beginningColumns = columns.slice(0, indexOfTagsSection)
    const middleColumns = dynamicTagColumns.map((categoryLabel) => ({
      label: categoryLabel,
      accessor: `tags-${categoryLabel}`,
      is_hidden: false,
      isSortable: false,
      parent: 'tags',
      format: (tagsForCategory = []) => {
        if (isEmpty(tagsForCategory)) {
          return null
        }

        return (
          <div className="tags-column-entry">
            {sortBy(tagsForCategory).map((tag) => (
              <Pill key={tag} small brand data-testid="tag">
                {tag}
              </Pill>
            ))}
          </div>
        )
      },
    }))
    const trailingColumns = columns.slice(indexOfTagsSection + 1)
    completeColumns = [...beginningColumns, ...middleColumns, ...trailingColumns]
  }

  return completeColumns
}

export const getChildColumns = (completeColumns, savedCustomColumns) => {
  return completeColumns
    .filter((col) => !isNil(col.parent))
    .map((col) => {
      const savedExtraColumn = savedCustomColumns.find((col1) => {
        return col1.accessor === col.accessor
      })
      if (isNil(savedExtraColumn)) {
        return col
      }
      return savedExtraColumn
    })
}
